'use client';

// Contact Form widget — name, email, phone, subject, message with submit.
import { useState } from 'react';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/storefront/ui/button';
import { Field, Input, Textarea } from '@/components/storefront/ui/input';
import { type ContactFieldError, submitContactForm } from '@/lib/contact/actions';
import { trackLead } from '@/lib/tracking/events';
import { CONTACT_FORM_FIELDS } from '@/lib/structured-builder/widget-types/contact-form';
import type { ContactFormConfig, ContactFormField } from '@/lib/structured-builder/widget-types/contact-form';
type Props = {
  config: ContactFormConfig;
  locale: string;
  nodeId?: string;
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FIELD_ERRORS: Record<ContactFieldError, {
  ar: string;
  en: string;
}> = {
  required: {
    ar: 'هذا الحقل مطلوب.',
    en: 'This field is required.'
  },
  invalid_email: {
    ar: 'يرجى إدخال بريد إلكتروني صحيح.',
    en: 'Please enter a valid email address.'
  }
};
const SEND_ERROR = {
  ar: 'تعذّر إرسال الرسالة الآن، يرجى المحاولة لاحقاً.',
  en: "Couldn't send your message right now. Please try again later."
};
const AUTOCOMPLETE: Partial<Record<ContactFormField, string>> = {
  name: 'name',
  email: 'email',
  phone: 'tel'
};

// Short fields pair up on wide screens; subject and message take the full row.
const HALF_WIDTH = new Set<ContactFormField>(['name', 'email', 'phone']);
export function ContactFormClient({
  config,
  locale,
  nodeId
}: Props) {
  const isAr = locale === 'ar';
  const lang = isAr ? 'ar' : 'en';
  const [values, setValues] = useState<Record<string, string>>({});
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<Partial<Record<ContactFormField, ContactFieldError>>>({});
  const [sendFailed, setSendFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const heading = isAr ? config.heading_ar : config.heading_en;
  const subtitle = isAr ? config.subtitle_ar : config.subtitle_en;
  const submitLabel = isAr ? config.submit_label_ar : config.submit_label_en;
  const successMsg = isAr ? config.success_message_ar : config.success_message_en;
  const alignCls = config.align === 'center' ? 'mx-auto' : '';
  const required = new Set(config.required_fields);
  function validate(): Partial<Record<ContactFormField, ContactFieldError>> {
    const found: Partial<Record<ContactFormField, ContactFieldError>> = {};
    for (const key of config.fields) {
      const v = (values[key] ?? '').trim();
      if (required.has(key) && !v) found[key] = 'required';else if (key === 'email' && v && !EMAIL_RE.test(v)) found[key] = 'invalid_email';
    }
    return found;
  }
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (sending) return;
    setSendFailed(false);
    const found_0 = validate();
    setErrors(found_0);
    if (Object.keys(found_0).length) return;
    setSending(true);
    try {
      const result = await submitContactForm({
        values,
        nodeId,
        website
      });
      if (result.status === 'sent') {
        setSubmitted(true);
        trackLead('contact_form');
      } else if (result.status === 'invalid') setErrors(result.fields);else setSendFailed(true);
    } catch {
      setSendFailed(true);
    } finally {
      setSending(false);
    }
  }
  const cardCls = config.style === 'card' ? 'bg-card border border-border rounded-2xl p-6 md:p-8' : '';
  return <div className={`w-full max-w-2xl ${alignCls}`}>
      {(heading || subtitle) && <div className={`mb-6 ${config.align === 'center' ? 'text-center' : ''}`}>
          {heading && <h2 className="text-2xl md:text-3xl font-bold text-[var(--widget-fg,var(--foreground))] text-balance">{heading}</h2>}
          {subtitle && <p className="text-base text-[var(--widget-fg,var(--muted-foreground))] mt-2 text-pretty">{subtitle}</p>}
        </div>}

      {submitted ? <output className={`flex flex-col items-center gap-3 py-10 text-center ${cardCls}`}>
          <CheckCircle size={48} weight="fill" className="text-primary" aria-hidden />
          <span className="text-lg font-semibold text-foreground">{successMsg}</span>
        </output> : <form onSubmit={handleSubmit} noValidate className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${cardCls}`}>
          {config.fields.map(fieldKey => {
        const def = CONTACT_FORM_FIELDS.find(f => f.key === fieldKey);
        if (!def) return null;
        const error = errors[fieldKey];
        const onChange = (v_0: string) => {
          setValues(prev => ({
            ...prev,
            [fieldKey]: v_0
          }));
          if (error) setErrors(prev_0 => ({
            ...prev_0,
            [fieldKey]: undefined
          }));
        };
        return <div key={fieldKey} className={HALF_WIDTH.has(fieldKey) ? '' : 'md:col-span-2'}>
                <Field label={isAr ? def.label_ar : def.label_en} required={required.has(fieldKey)} error={error ? FIELD_ERRORS[error][lang] : undefined}>
                  {(id, _describedBy, aria) => def.type === 'textarea' ? <Textarea id={id} name={fieldKey} rows={5} value={values[fieldKey] ?? ''} onChange={e_0 => onChange(e_0.target.value)} invalid={!!error} {...aria} /> : <Input id={id} name={fieldKey} type={def.type} autoComplete={AUTOCOMPLETE[fieldKey]} inputMode={def.type === 'tel' ? 'tel' : def.type === 'email' ? 'email' : undefined} dir={def.type === 'email' || def.type === 'tel' ? 'ltr' : undefined} className={def.type === 'email' || def.type === 'tel' ? 'rtl:text-right' : undefined} value={values[fieldKey] ?? ''} onChange={e_1 => onChange(e_1.target.value)} invalid={!!error} {...aria} />}
                </Field>
              </div>;
      })}

          {/* Honeypot: off-screen and hidden from assistive tech; only bots fill it. */}
          <div aria-hidden="true" className="absolute -start-[9999px] h-px w-px overflow-hidden">
            <label>
              Website
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={e_2 => setWebsite(e_2.target.value)} />
            </label>
          </div>

          {sendFailed && <p role="alert" className="md:col-span-2 inline-flex items-center gap-1.5 text-sm text-danger">
              <WarningCircle size={16} weight="fill" className="shrink-0" aria-hidden />
              {SEND_ERROR[lang]}
            </p>}

          <div className="md:col-span-2">
            <Button variant="solid" color={config.button_color} size="lg" type="submit" loading={sending} className="w-full sm:w-auto sm:min-w-40">
              {submitLabel}
            </Button>
          </div>
        </form>}
    </div>;
}